import { useEffect, useSyncExternalStore } from 'react';
import { CONTENT_MAX_WIDTH } from '@wundo/ui';

/*
 * How narrow this page's content may be squeezed before the chat drawer has to
 * lie over it. One page on screen, so one answer — held in the module rather
 * than in a context, for the same reason the chat's anchor and its page line
 * are: the page that knows this and the shell that lays out for it are nowhere
 * near each other in the tree.
 */
let current: number | null = null;

const listeners = new Set<() => void>();

/**
 * The floor for the page on screen.
 *
 * The full content column unless a page has said otherwise: squeezing a page
 * that was designed for 960px is worse than putting the drawer over it.
 */
export function currentChatContentMin(): number {
  return current ?? CONTENT_MAX_WIDTH;
}

/** Say how far this page may give way. Pages do this through the hook below. */
export function setChatContentMin(min: number | null): void {
  current = min;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/**
 * Publish this page's floor for as long as the page is on screen.
 *
 * Cleared on the way out, because a page that reads fine in a narrow column
 * must not lend that to the next one. Only cleared when the floor still is
 * this page's own — a page that has already handed over to another must not
 * wipe the newer one.
 */
export function usePublishChatContentMin(min: number): void {
  useEffect(() => {
    setChatContentMin(min);

    return () => {
      if (current === min) setChatContentMin(null);
    };
  }, [min]);
}

/** The floor, as the shell reads it: reactively, from wherever it is drawn. */
export function useChatContentMin(): number {
  return useSyncExternalStore(subscribe, currentChatContentMin, () => CONTENT_MAX_WIDTH);
}
